// lib/CookieManager.ts
import { IncomingMessage } from 'http';
import AESHelper from "@/Fwk/Encrypt/AESHelper";
import Util from "@/Master/Util";

export interface CookieOptions {
    path?: string;
    expires?: Date | number;
    maxAge?: number;
    domain?: string;
    secure?: boolean;
    httpOnly?: boolean;
    sameSite?: 'strict' | 'lax' | 'none';
}

class CookieMgr {

    // static isEncrypt = true;
    /**
     * 设置 Cookie
     * @param name - Cookie 名称
     * @param value - Cookie 值
     * @param options - 可选的 Cookie 选项
     * @param res - 可选的服务器端响应对象，用于设置 Set-Cookie 头
     */
    static setCookie(name: string, value: string, options: CookieOptions = {}, res?: any) {
        const cookieString = this.serializeCookie(name, value, options);
        if (typeof window !== 'undefined') {
            // 客户端设置 Cookie
            document.cookie = cookieString;
        } else if (res) {
            // 服务器端设置 Cookie，通过响应头
            let existingCookies = res.getHeader('Set-Cookie') as string[] | undefined;
            if (!existingCookies) {
                existingCookies = [];
            }
            existingCookies.push(cookieString);
            res.setHeader('Set-Cookie', existingCookies);
        }
    }

    /**
     * 获取 Cookie
     * @param name - Cookie 名称
     * @param req - 可选的服务器端请求对象，用于解析 Cookie
     * @returns Cookie 值或 null
     */
    static getCookie(name: string, req?: IncomingMessage): string | null {
        if (typeof window !== 'undefined') {
            // 客户端获取 Cookie
            return this.getCookieFromString(document.cookie, name);
        } else if (req && req.headers.cookie) {
            // 服务器端获取 Cookie
            return this.getCookieFromString(req.headers.cookie, name);
        }
        return null;
    }

    /**
     * 删除 Cookie
     * @param name - Cookie 名称
     * @param options - 可选的 Cookie 选项（通常至少包含 path）
     * @param res - 可选的服务器端响应对象，用于设置 Set-Cookie 头
     */
    static removeCookie(name: string, options: CookieOptions = {}, res?: any) {
        // 设置过期时间为过去的时间
        const opts = { ...options, expires: new Date(0) };
        this.setCookie(name, '', opts, res);
    }

    /**
     * 解析 Cookie 字符串并获取指定 Cookie 的值
     * @param cookieString - 完整的 Cookie 字符串
     * @param name - 要获取的 Cookie 名称
     * @returns Cookie 值或 null
     */
    private static getCookieFromString(cookieString: string, name: string): string | null {
        const cookies = cookieString.split(';').map(cookie => cookie.trim());
        for (const cookie of cookies) {
            if (cookie.startsWith(`${encodeURIComponent(name)}=`)) {
                let value = decodeURIComponent(cookie.substring(name.length + 1));
                // if (this.isEncrypt){
                //     value = AESHelper.DecryptWeb(value)
                // }
                return value;
            }
        }
        return null;
    }

    /**
     * 序列化 Cookie
     * @param name - Cookie 名称
     * @param value - Cookie 值
     * @param options - 可选的 Cookie 选项
     * @returns 序列化后的 Cookie 字符串
     */
    private static serializeCookie(name: string, value: string, options: CookieOptions): string {
        // let newName = Util.DeepClone(name);
        let cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

        if (options.expires) {
            if (options.expires instanceof Date) {
                cookie += `; Expires=${options.expires.toUTCString()}`;
            } else {
                // options.expires 是 number，表示秒数
                const expiresDate = new Date(Date.now() + options.expires * 1000);
                cookie += `; Expires=${expiresDate.toUTCString()}`;
            }
        }

        if (options.maxAge) {
            cookie += `; Max-Age=${options.maxAge}`;
        }

        if (options.domain) {
            cookie += `; Domain=${options.domain}`;
        }

        if (options.path) {
            cookie += `; Path=${options.path}`;
        } else {
            cookie += `; Path=/`;
        }

        if (options.secure) {
            cookie += `; Secure`;
        }

        if (options.httpOnly) {
            cookie += `; HttpOnly`;
        }

        if (options.sameSite) {
            cookie += `; SameSite=${options.sameSite}`;
        }

        return cookie;
    }
}

export default CookieMgr;


